import { z } from 'zod'

function formatPercentageToken(value: number): string {
  if (!Number.isFinite(value)) {
    return ''
  }
  if (Number.isInteger(value)) {
    return String(value)
  }
  const text = value.toFixed(4)
  return text.replace(/\.?0+$/, '')
}

function parseRecyclingPctNumeric(raw: string): number | null {
  let compact = raw.trim().replace(/\s/g, '').replace(/%$/, '')
  if (compact.length === 0) {
    return 0
  }

  compact = compact.replace(',', '.')
  if (compact.startsWith('.')) {
    compact = `0${compact}`
  }
  if (/^\d+\.$/.test(compact)) {
    compact = compact.slice(0, -1)
  }
  if (!/^\d+(\.\d+)?$/.test(compact)) {
    return null
  }

  const value = Number.parseFloat(compact)
  if (!Number.isFinite(value) || value < 0 || value > 100) {
    return null
  }

  return value
}

/** Stable string for controlled text inputs from stored percentage values. */
export function formatComponentRecyclingPctForFormInput(value: string): string {
  const normalized = normalizeComponentRecyclingPctField(value)
  if (normalized === null) {
    return '0'
  }
  return normalized
}

/** Display recycling percentage in the components table. */
export function formatComponentRecyclingPctDisplay(value: string): string {
  const trimmed = value.trim()
  if (trimmed.length === 0) {
    return '—'
  }
  if (trimmed.endsWith('%')) {
    return trimmed
  }
  return `${trimmed}%`
}

export function normalizeComponentRecyclingPctField(
  raw: string
): string | null {
  const parsed = parseRecyclingPctNumeric(raw)
  if (parsed === null) {
    return null
  }
  return formatPercentageToken(parsed)
}

export const componentRecyclingPctInputSchema = z
  .string()
  .trim()
  .transform((value, context) => {
    const normalized = normalizeComponentRecyclingPctField(value)
    if (normalized === null) {
      context.addIssue({
        code: 'custom',
        message: 'invalid',
      })
      return z.NEVER
    }
    return normalized
  })

export type ComponentRecyclingPctInputError = 'invalid'

export function getComponentRecyclingPctInputError(
  raw: string
): ComponentRecyclingPctInputError | null {
  if (normalizeComponentRecyclingPctField(raw) === null) {
    return 'invalid'
  }
  return null
}
